import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { EVENT_COLORS } from '../../config'
import type { CalendarEvent, EventFormData } from '../../types'

interface EventModalProps {
  open: boolean
  initial: EventFormData
  editing: CalendarEvent | null
  onClose: () => void
  onSave: (data: EventFormData) => void
  onDelete?: () => void
}

export function EventModal({
  open,
  initial,
  editing,
  onClose,
  onSave,
  onDelete,
}: EventModalProps) {
  const [form, setForm] = useState<EventFormData>(initial)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setForm(initial)
      setError('')
    }
  }, [open, initial])

  if (!open) return null

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) {
      setError('Bitte einen Titel eingeben.')
      return
    }
    if (form.end < form.start) {
      setError('Ende muss nach dem Start liegen.')
      return
    }
    onSave(form)
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-modal-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="modal__header">
          <h2 id="event-modal-title">
            {editing ? 'Termin bearbeiten' : 'Neuer Termin'}
          </h2>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ✕
          </button>
        </header>

        <form className="modal__form" onSubmit={submit}>
          <label className="field">
            <span>Titel</span>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="z.B. Meeting"
              autoFocus
              required
            />
          </label>

          <div className="field-row">
            <label className="field">
              <span>Start</span>
              <input
                type="datetime-local"
                value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
                required
              />
            </label>
            <label className="field">
              <span>Ende</span>
              <input
                type="datetime-local"
                value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })}
                required
              />
            </label>
          </div>

          <label className="field">
            <span>Notizen (optional)</span>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Details…"
            />
          </label>

          <fieldset className="field color-field">
            <legend>Farbe</legend>
            <div className="color-swatches">
              {EVENT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`swatch${form.color === c ? ' swatch--active' : ''}`}
                  style={{ background: c }}
                  aria-label={`Farbe ${c}`}
                  aria-pressed={form.color === c}
                  onClick={() => setForm({ ...form, color: c })}
                />
              ))}
            </div>
          </fieldset>

          {error && <p className="form-error">{error}</p>}

          <div className="modal__actions">
            {editing && onDelete && (
              <button
                type="button"
                className="neon-btn neon-btn--danger"
                onClick={onDelete}
              >
                Löschen
              </button>
            )}
            <div className="modal__actions-right">
              <button
                type="button"
                className="neon-btn neon-btn--ghost"
                onClick={onClose}
              >
                Abbrechen
              </button>
              <button type="submit" className="neon-btn neon-btn--primary">
                Speichern
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
